import { CommonModule } from '@angular/common';
import { Component, forwardRef, input } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { finalize, forkJoin, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AddressLine, FuelType, MeterDetails, MeterSelection, MeterType, Meter } from '@models/meter.model';
import { MeterService } from '@services/meter.service';
import { UtilService } from '@services/util.service';
import { CheckboxGroupComponent } from '@components/shared/checkbox-group.component';

@Component({
  selector: 'app-meter-selection',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatRadioModule,
    MatSelectModule,
    MatProgressBarModule,
    MatIconModule,
    CheckboxGroupComponent
  ],
  templateUrl: './meter-selection.component.html',
  styleUrls: ['./meter-selection.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MeterSelectionComponent),
      multi: true
    }
  ]
})
export class MeterSelectionComponent implements ControlValueAccessor {
  form: FormGroup;
  addressList: AddressLine[] = [];
  meterDetails: MeterDetails[] = [];
  fetchingMeterDetails = false;
  fetchingAddressLines = false;
  canShowNoMetersErrorFlag = false;
  meterAlreadyExistsFlag = false;
  selectedAddress: AddressLine | null = null;
  fuelTypeEnum = FuelType;

  initialValue = input<Partial<MeterSelection>>();
  supportedMeterTypes = input<MeterType[]>([]);

  // ControlValueAccessor implementation
  private onChange: (value: MeterDetails[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private fb: FormBuilder,
    private meterService: MeterService,
    private utilService: UtilService
  ) {
    this.form = this.fb.group({
      addressType: ['postcode', Validators.required],
      addressSearch: this.fb.group({
        postcode: ['', [Validators.required, Validators.minLength(6)]],
        address: ['', Validators.required]
      }),
      meterNoSearch: this.fb.group({
        meterNo: ['', Validators.required],
        fuelType: [FuelType.ELEC, Validators.required]
      }),
      selectedMeter: [[], Validators.required]
    });
  }

  ngOnInit(): void {
    // Initialize form with initial value if provided
    const initialFormValue = this.initialValue();
    if (initialFormValue) {
      this.form.patchValue(initialFormValue, { emitEvent: false });
      if (initialFormValue.addressType === 'postcode') {
        this.searchPostcode();
      } else if (initialFormValue.addressType === 'meterNo') {
        this.searchMeterNo();
      }
    }

    // Set up form change handlers
    // this.form.get('addressType')?.valueChanges.subscribe(val => {
    //   if (val === 'postcode') {
    //     this.form.get('meterNoSearch')?.disable();
    //     this.form.get('addressSearch')?.reset();
    //     this.addressList = [];
    //     this.form.get('addressSearch')?.enable();
    //     this.form.get('addressSearch.fuelType')?.disable();
    //     this.showAddressFuelTypeSelection = false;
    //     this.selectedAddress = null;
    //   } else if (val === 'meterNo') {
    //     this.form.get('addressSearch')?.disable();
    //     this.form.get('meterNoSearch')?.reset({
    //       meterNo: '',
    //       fuelType: FuelType.ELEC
    //     });
    //     this.form.get('meterNoSearch')?.enable();
    //     this.showAddressFuelTypeSelection = false;
    //     this.selectedAddress = null;
    //   }
    //   this.meterDetails = [];
    //   this.form.get('selectedMeter')?.reset();
    // });

    // Update parent form when selected meter changes
    this.form.get('selectedMeter')?.valueChanges.subscribe(value => {
      this.onChange(value || []);
    });
  }

  // ControlValueAccessor methods
  writeValue(value: MeterDetails[]): void {
    if (value) {
      this.form.get('selectedMeter')?.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  searchPostcode(): void {
    this.fetchingAddressLines = true;
    this.meterDetails = [];
    this.selectedAddress = null;
    this.canShowNoMetersErrorFlag = false;
    this.meterAlreadyExistsFlag = false;

    const postcode = this.form.get('addressSearch.postcode')?.value;
    if (postcode) {
      this.meterService.getAddressLine(postcode)
        .pipe(
          finalize(() => this.fetchingAddressLines = false)
        )
        .subscribe({
          next: (response) => {
            this.addressList = response;
            this.form.get('selectedMeter')?.reset();
          },
          error: (error) => {
            this.utilService.openSnackBar(
              'Error fetching addresses. Please check the postcode and try again.',
              'Close',
              'error'
            );
            console.error('Error fetching addresses:', error);
          }
        });
    }
  }

  searchMeterNo(): void {
    // this.fetchingMeterDetails = true;
    const meterNo = this.form.get('meterNoSearch.meterNo')?.value;
    const fuelType = this.form.get('meterNoSearch.fuelType')?.value;
    this.canShowNoMetersErrorFlag = false;
    this.meterAlreadyExistsFlag = false;

    if (meterNo && fuelType) {
      // First check if the meter already exists in cached meters
      this.checkIfMeterAlreadyExists(meterNo, fuelType)
        .subscribe({
          next: (meterExists: boolean) => {
            if (meterExists) {
              // Meter already exists, show notification and set flag
              // this.utilService.openSnackBar(
              //   `This ${fuelType} meter (${meterNo}) is already added to your account.`,
              //   'Close',
              //   'error'
              // );
              this.meterDetails = [];
              this.meterAlreadyExistsFlag = true;
            } else {
              // Meter doesn't exist, proceed with normal search
              this.fetchMeterDetailsFromApi(meterNo, fuelType);
            }
          },
          error: (error: any) => {
            console.error('Error checking existing meters:', error);
            // If checking existing meters fails, proceed with normal search
            this.fetchMeterDetailsFromApi(meterNo, fuelType);
          }
        });
    }
  }

  private checkIfMeterAlreadyExists(meterNo: string, fuelType: FuelType): Observable<boolean> {
    return this.meterService.getMeters(false)
      .pipe(
        map((existingMeters: Meter[]) => {
          // Check if the entered meter number already exists
          const existingMeter = existingMeters.find((meter: Meter) => 
            meter.meterNumber === meterNo && 
            meter.fuelType.toLowerCase() === fuelType.toLowerCase()
          );
          
          return !!existingMeter; // Return boolean indicating if meter exists
        }),
        catchError((error: any) => {
          console.error('Error checking existing meters:', error);
          // If checking existing meters fails, assume meter doesn't exist
          return of(false);
        })
      );
  }

  private fetchMeterDetailsFromApi(meterNo: string, fuelType: FuelType): void {
    this.fetchingMeterDetails = true;
    this.meterService.getMeterDetailsByMeterNo(meterNo, fuelType)
      .pipe(
        finalize(() => this.fetchingMeterDetails = false)
      )
      .subscribe({
        next: (response) => {
          this.meterDetails = response;
          this.form.get('selectedMeter')?.reset();
          const supportedMeters = this.getSupportedMeters();
          if (supportedMeters.length > 0) {
            if (supportedMeters.length === 1) {
              this.form.get('selectedMeter')?.setValue([supportedMeters[0]]);
            }
            this.form.get('selectedMeter')?.enable();
          } else {
            this.form.get('selectedMeter')?.disable();
            this.canShowNoMetersErrorFlag = true;
          }
        },
        error: (error) => {
          console.error('Error fetching meter details:', error);
          this.utilService.openSnackBar(
            'Error fetching meter details. Please check the meter number and try again.',
            'Close',
            'error'
          );
          this.meterDetails = [];
        }
      });
  }

  onAddressSelected(): void {
    const addressId = this.form.get('addressSearch.address')?.value;
    this.selectedAddress = this.addressList.find(addr => addr.addressId === addressId) || null;
    this.canShowNoMetersErrorFlag = false;
    this.meterAlreadyExistsFlag = false;
    this.form.get('selectedMeter')?.reset();
    this.meterDetails = [];

    if (this.selectedAddress) {
      const hasElectricity = this.selectedAddress.numberMPAN > 0;
      const hasGas = this.selectedAddress.numberMPRN > 0;
      
      if (hasElectricity || hasGas) {
        this.fetchAllMeterDetailsByAddress();
      } else {
        this.utilService.openSnackBar(
          'No meters found at this address.',
          'Close',
          'error'
        );
      }
    }
  }

  private fetchAllMeterDetailsByAddress(): void {
    this.fetchingMeterDetails = true;
    const addressId = this.selectedAddress?.addressId;
    this.canShowNoMetersErrorFlag = false;
    this.meterAlreadyExistsFlag = false;
    
    if (addressId && this.selectedAddress) {
      const hasElectricity = this.selectedAddress.numberMPAN > 0;
      const hasGas = this.selectedAddress.numberMPRN > 0;
      
      const requests = [];
      
      if (hasElectricity) {
        requests.push(this.meterService.getMeterDetailsByAddressId(addressId, FuelType.ELEC));
      }
      
      if (hasGas) {
        requests.push(this.meterService.getMeterDetailsByAddressId(addressId, FuelType.GAS));
      }
      
      if (requests.length > 0) {
        forkJoin(requests)
          .pipe(
            finalize(() => this.fetchingMeterDetails = false)
          )
          .subscribe({
            next: (responses) => {
              // Flatten all meter details from both fuel types
              const allMeterDetails = responses.flat();
              
              // Get existing meters to filter out already added ones
              this.meterService.getMeters(false).subscribe({
                next: (existingMeters: Meter[]) => {
                  // Filter out meters that already exist
                  const newMeterDetails = allMeterDetails.filter(meterDetail => {
                    return !existingMeters.some(existingMeter => 
                      existingMeter.meterNumber === meterDetail.meterNo && 
                      existingMeter.fuelType.toLowerCase() === meterDetail.fuelType.toLowerCase()
                    );
                  });
                  
                  this.meterDetails = newMeterDetails;
                  this.form.get('selectedMeter')?.reset();
                  
                  // Check if all meters were filtered out (already exist)
                  if (allMeterDetails.length > 0 && newMeterDetails.length === 0) {
                    // All meters at this address are already added
                    this.meterAlreadyExistsFlag = true;
                  } else {
                    const supportedMeters = this.getSupportedMeters();
                    if (supportedMeters.length > 0) {
                      if (supportedMeters.length === 1) {
                        this.form.get('selectedMeter')?.setValue([supportedMeters[0]]);
                      }
                      this.form.get('selectedMeter')?.enable();
                    } else {
                      this.form.get('selectedMeter')?.disable();
                      this.canShowNoMetersErrorFlag = true;
                    }
                  }
                },
                error: (error: any) => {
                  console.error('Error checking existing meters:', error);
                  // If checking existing meters fails, proceed with all meter details
                  this.meterDetails = allMeterDetails;
                  this.form.get('selectedMeter')?.reset();
                  
                  const supportedMeters = this.getSupportedMeters();
                  if (supportedMeters.length > 0) {
                    if (supportedMeters.length === 1) {
                      this.form.get('selectedMeter')?.setValue([supportedMeters[0]]);
                    }
                    this.form.get('selectedMeter')?.enable();
                  } else {
                    this.form.get('selectedMeter')?.disable();
                    this.canShowNoMetersErrorFlag = true;
                  }
                }
              });
            },
            error: (error) => {
              console.error('Error fetching meter details:', error);
              this.utilService.openSnackBar(
                'Error fetching meter details. Please try again.',
                'Close',
                'error'
              );
              this.meterDetails = [];
            }
          });
      }
    }
  }

  // Utility methods
  canShowNoMetersError(): boolean {
    // Show error if no meter details found, or if meter details exist but no supported meters
    return this.canShowNoMetersErrorFlag || (this.meterDetails.length > 0 && this.getSupportedMeters().length === 0);
  }

  canShowMeterAlreadyExistsError(): boolean {
    return this.meterAlreadyExistsFlag;
  }

  hasSmartMeters(): boolean {
    return this.meterDetails.some(meter => meter.meterType === MeterType.SMART);
  }

  hasNonSupportedMeters(): boolean {
    const supportedTypes = this.supportedMeterTypes();
    if (supportedTypes.length === 0) {
      // If no supported types specified, all meters are considered supported
      return false;
    }
    // Check if there are any meters that are not in the supported types
    return this.meterDetails.some(meter => !supportedTypes.includes(meter.meterType));
  }

  getSupportedMeters(): MeterDetails[] {
    const supportedTypes = this.supportedMeterTypes();
    if (supportedTypes.length === 0) {
      // If no supported types specified, return all meters
      return this.meterDetails;
    }
    // Filter meters based on supported types
    return this.meterDetails.filter(meter => supportedTypes.includes(meter.meterType));
  }

  // Convert meters to checkbox options for the checkbox group component
  getMeterCheckboxOptions() {
    return this.getSupportedMeters().map(meter => ({
      label: `${meter.meterType} - ${meter.meterNo} (${meter.fuelType})`,
      value: meter
    }));
  }

  // Optional: Handle checkbox change events if additional logic is needed
  onMeterCheckboxChange(event: any) {
    // You can add any additional logic here if needed
    // console.log('Meter selection changed:', event);
  }
}
